import { QueryClient } from "@tanstack/react-query";

import { BidReportEntry_Answer_Sources } from "lib/generated/app-service-gql/graphql";
import {
  BidNoBidResponse,
  updateBidReportAnswersCache,
} from "../../hooks/api/bid/answers/useGetBidReportAnswers";
import { BidOrchestratorSection } from "../../types/graphQLEnums";
import { BidOrchestratorEntry, BidReportRelease, PendingAnswer } from "./types";

/**
 * Handles a `streaming_chunk` event for a per-question section. Reveals are gated
 * in canonical question order, but the question at the front of the queue streams
 * its content straight to the cache so the answer types in. Chunks for questions
 * further down the queue are dropped if mid-stream and buffered if complete; when
 * the current question finishes we advance the cursor and drain any buffered
 * completes that have piled up behind it. Replays of already-released chunks are
 * silent no-ops.
 */
export function updateStreamedReportAnswer({
  queryClient,
  nextEntry,
  sectionType,
  streamedAnswer,
  bidQuestionId,
  isComplete,
  sources,
}: {
  queryClient: QueryClient;
  nextEntry: BidOrchestratorEntry;
  sectionType: keyof BidOrchestratorEntry["bidReportRelease"];
  streamedAnswer: string;
  bidQuestionId: string | null | undefined;
  isComplete?: boolean | null;
  sources?: BidReportEntry_Answer_Sources[] | null;
}): BidOrchestratorEntry {
  if (!bidQuestionId) {
    console.warn(
      `Received streaming chunk for ${sectionType} with missing bidQuestionId - skipping`,
    );
    return nextEntry;
  }

  const section = nextEntry.bidReportRelease[sectionType];
  const orderIndex = section.sortedQuestions.findIndex((q) => q.bidQuestionId === bidQuestionId);
  if (orderIndex === -1) {
    console.warn(
      `Streaming chunk for ${sectionType} with unknown bidQuestionId ${bidQuestionId} - skipping`,
    );
    return nextEntry;
  }
  // Replay of an already-released question — silent no-op.
  if (orderIndex < section.nextReleaseIndex) {
    return nextEntry;
  }

  const bidReportId =
    sectionType === BidOrchestratorSection.QualificationReport
      ? nextEntry.qualificationReportId
      : nextEntry.requirementSummaryReportId;
  if (!bidReportId) {
    console.warn(
      `Streaming chunk for ${sectionType} but report ID is not set - skipping cache update`,
    );
    return nextEntry;
  }

  // Future question — hide mid-stream chunks behind the gate. If this is the
  // terminal chunk, stash it so the drain loop can pick it up once earlier
  // questions finish.
  if (orderIndex > section.nextReleaseIndex) {
    if (isComplete !== true) return nextEntry;
    return {
      ...nextEntry,
      bidReportRelease: {
        ...nextEntry.bidReportRelease,
        [sectionType]: {
          ...section,
          pendingAnswers: {
            ...section.pendingAnswers,
            [bidQuestionId]: { content: streamedAnswer, sources: sources ?? null },
          },
        },
      },
    };
  }

  // Current question: write every chunk so the answer types in. Length-wins
  // protects against a re-delivered earlier chunk rolling back later content.
  const { groupName } = section.sortedQuestions[orderIndex];
  writeAnswerToCache(queryClient, bidReportId, {
    bidQuestionId,
    groupName,
    answer: streamedAnswer,
    sources: isComplete ? sources ?? null : null,
    isComplete: isComplete === true,
  });

  if (isComplete !== true) {
    // Mid-stream chunk for the current question — cache is updated, entry stays put.
    return nextEntry;
  }

  // Current question finished. Advance the cursor and drain any buffered
  // completes that have been waiting behind it.
  const { nextReleaseIndex, pendingAnswers } = drainBufferedCompletes(
    queryClient,
    bidReportId,
    section,
    orderIndex + 1,
  );

  return {
    ...nextEntry,
    bidReportRelease: {
      ...nextEntry.bidReportRelease,
      [sectionType]: { ...section, nextReleaseIndex, pendingAnswers },
    },
  };
}

/**
 * Closes out the release machinery for a per-question section on its
 * per-section `*_generation_success` event. Drops any still-buffered completes
 * and parks the cursor past the end so straggler chunks arriving after
 * hydration cannot overwrite the persisted shape.
 */
export function closeSectionRelease(
  entry: BidOrchestratorEntry,
  sectionType: keyof BidOrchestratorEntry["bidReportRelease"],
): BidOrchestratorEntry {
  const section = entry.bidReportRelease[sectionType];
  return {
    ...entry,
    bidReportRelease: {
      ...entry.bidReportRelease,
      [sectionType]: {
        ...section,
        nextReleaseIndex: section.sortedQuestions.length,
        pendingAnswers: {},
      },
    },
  };
}

/**
 * Releases every contiguous buffered complete starting at `fromIndex` to the
 * cache, returning the new cursor position and the buffer with released
 * entries removed.
 */
function drainBufferedCompletes(
  queryClient: QueryClient,
  bidReportId: string,
  section: BidReportRelease,
  fromIndex: number,
): { nextReleaseIndex: number; pendingAnswers: Record<string, PendingAnswer> } {
  const pendingAnswers = { ...section.pendingAnswers };
  let nextReleaseIndex = fromIndex;

  while (nextReleaseIndex < section.sortedQuestions.length) {
    const { bidQuestionId, groupName } = section.sortedQuestions[nextReleaseIndex];
    const pending = pendingAnswers[bidQuestionId];
    if (!pending) break;

    writeAnswerToCache(queryClient, bidReportId, {
      bidQuestionId,
      groupName,
      answer: pending.content,
      sources: pending.sources,
      isComplete: true,
    });
    delete pendingAnswers[bidQuestionId];
    nextReleaseIndex += 1;
  }

  return { nextReleaseIndex, pendingAnswers };
}

type AnswerWrite = {
  bidQuestionId: string;
  groupName: string;
  answer: string;
  sources: BidReportEntry_Answer_Sources[] | null;
  isComplete: boolean;
};

function writeAnswerToCache(queryClient: QueryClient, bidReportId: string, write: AnswerWrite) {
  updateBidReportAnswersCache(queryClient, bidReportId, (prev) => {
    const groupIndex = prev.result.findIndex((g) => g.groupName === write.groupName);
    if (groupIndex === -1) {
      console.warn(
        `Streaming chunk for group ${write.groupName} which is not in the persisted report - skipping cache update`,
      );
      return prev;
    }
    const group = prev.result[groupIndex];
    const questionIndex = group.questions.findIndex((q) => q.answerId === write.bidQuestionId);
    if (questionIndex === -1) {
      console.warn(
        `Streaming chunk for question ${write.bidQuestionId} which is not in the persisted report - skipping cache update`,
      );
      return prev;
    }

    // Length-wins replay safety: a re-delivered earlier chunk shouldn't roll
    // back longer content already in the cache. Always apply on isComplete so
    // sources land even when the text length is unchanged.
    const existing = group.questions[questionIndex].answer?.answer ?? "";
    if (!write.isComplete && write.answer.length <= existing.length) return prev;

    return {
      ...prev,
      result: mergeAnswerIntoReport(
        prev.result,
        groupIndex,
        questionIndex,
        write.answer,
        write.sources,
      ),
    };
  });
}

/**
 * Immutable update along the (result → group → question) path only. Groups and
 * questions outside that path retain their references, so memoised consumers
 * skip re-rendering branches that didn't change.
 */
function mergeAnswerIntoReport(
  previousResponse: BidNoBidResponse,
  groupIndex: number,
  questionIndex: number,
  updatedAnswer: string,
  sources: BidReportEntry_Answer_Sources[] | null,
): BidNoBidResponse {
  return previousResponse.map((g, gi) =>
    gi !== groupIndex
      ? g
      : {
          ...g,
          questions: g.questions.map((q, qi) => {
            if (qi !== questionIndex) return q;
            return {
              ...q,
              answer: { answer: updatedAnswer },
              ...(sources !== null ? { sources } : {}),
            };
          }),
        },
  );
}
